import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  annullaFirma,
  avviaFirma,
  generaDocumentoDelega,
  getDelegaCheck,
  getFirmaStatus,
  getDocumentoPdf,
  getDocumentoFirmato,
} from "../../api/client";
import type { DelegaCheck } from "../../api/types";
import { FirmaBlock } from "../../components/FirmaBlock";
import { DetailPageShell } from "../../lib/DetailPageShell";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// Uppercase micro-label used above card sections (Delegante/Delegato/Attività/...).
const sectionLabelStyle: CSSProperties = {
  fontSize: "var(--font-size-xs)",
  fontWeight: "var(--font-weight-bold)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--indaco-gray-500)",
};

function VerdettoBadge({ stato }: { stato?: string | null }) {
  if (!stato) return null;
  const ok = stato === "LEGITTIMATO";
  return (
    <span className={`badge ${ok ? "badge-success" : "badge-danger"}`}>
      <span className="material-symbols-outlined icon-sm" style={{ marginRight: 4 }}>
        {ok ? "check_circle" : "cancel"}
      </span>
      {ok ? "LEGITTIMATO" : "NON LEGITTIMATO"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// InfoRow
// ---------------------------------------------------------------------------

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-1)", fontSize: "var(--font-size-sm)" }}>
      <span style={{ color: "var(--indaco-gray-500)", minWidth: 120, flexShrink: 0 }}>{label}</span>
      <span style={{ color: "var(--indaco-gray-800)", fontWeight: "var(--font-weight-medium)" }}>{value || "—"}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function DelegaDetail() {
  const { checkId } = useParams<{ checkId: string }>();
  const navigate = useNavigate();

  const [check, setCheck] = useState<DelegaCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [firmaLoading, setFirmaLoading] = useState(false);
  const [annullaLoading, setAnnullaLoading] = useState(false);
  const [firmatoLoading, setFirmatoLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Carica dettaglio ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!checkId) return;
    setLoading(true);
    getDelegaCheck(checkId)
      .then(setCheck)
      .catch(e => setError(e instanceof Error ? e.message : "Errore caricamento"))
      .finally(() => setLoading(false));
  }, [checkId]);

  // ── Polling firma quando in corso ─────────────────────────────────────────
  useEffect(() => {
    const fs = check?.firma_status;
    const isActive = fs === "attesa_delegante" || fs === "attesa_delegato";

    if (!isActive || !checkId) {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      return;
    }

    pollingRef.current = setInterval(async () => {
      try {
        const updated = await getFirmaStatus(checkId);
        setCheck(prev => prev ? {
          ...prev,
          firma_status: updated.firma_status,
          firma_avviata_at: updated.firma_avviata_at ?? prev.firma_avviata_at,
          firma_completata_at: updated.firma_completata_at ?? prev.firma_completata_at,
          firma_session_delegante_id: updated.firma_session_delegante_id ?? prev.firma_session_delegante_id,
          firma_session_delegato_id: updated.firma_session_delegato_id ?? prev.firma_session_delegato_id,
        } : prev);
      } catch { /* non-fatale */ }
    }, 15_000);

    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [check?.firma_status, checkId]);

  // ── Genera PDF ────────────────────────────────────────────────────────────
  async function handleGeneraPdf() {
    if (!checkId) return;
    setPdfLoading(true);
    setError(null);
    try {
      const blob = await generaDocumentoDelega(checkId);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setCheck(prev => prev ? { ...prev, has_documento: true } : prev);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore generazione PDF");
    } finally {
      setPdfLoading(false);
    }
  }

  // ── Scarica PDF già esistente ──────────────────────────────────────────────
  async function handleDownloadPdf() {
    if (!checkId) return;
    if (pdfUrl) { triggerDownload(pdfUrl); return; }
    setPdfLoading(true);
    try {
      const blob = await getDocumentoPdf(checkId);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      triggerDownload(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore download PDF");
    } finally {
      setPdfLoading(false);
    }
  }

  function triggerDownload(url: string) {
    const a = document.createElement("a");
    a.href = url;
    a.download = `procura_${checkId?.slice(0, 8) ?? "delega"}.pdf`;
    a.click();
  }

  // ── Avvia firma ───────────────────────────────────────────────────────────
  async function handleAvviaFirma() {
    if (!checkId) return;
    setFirmaLoading(true);
    setError(null);
    try {
      const fs = await avviaFirma(checkId);
      setCheck(prev => prev ? {
        ...prev,
        firma_status: fs.firma_status,
        firma_avviata_at: fs.firma_avviata_at,
        firma_session_delegante_id: fs.firma_session_delegante_id,
        wa_number: fs.wa_number || prev.wa_number,
      } : prev);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore avvio firma");
    } finally {
      setFirmaLoading(false);
    }
  }

  // ── Annulla firma ────────────────────────────────────────────────────────
  async function handleAnnullaFirma() {
    if (!checkId) return;
    setAnnullaLoading(true);
    setError(null);
    try {
      await annullaFirma(checkId);
      setCheck(prev => prev ? {
        ...prev,
        firma_status: null,
        firma_avviata_at: undefined,
        firma_session_delegante_id: undefined,
        firma_session_delegato_id: undefined,
      } : prev);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore annullamento firma");
    } finally {
      setAnnullaLoading(false);
    }
  }

  // ── Download documento firmato ───────────────────────────────────────────
  async function handleDownloadFirmato() {
    if (!checkId) return;
    setFirmatoLoading(true);
    setError(null);
    try {
      const blob = await getDocumentoFirmato(checkId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `procura_firmata_${checkId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore download documento firmato");
    } finally {
      setFirmatoLoading(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="loading-center" style={{ minHeight: 300 }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  if (!check) return (
    <div style={{ padding: "var(--space-8)" }}>
      <p className="text-muted">{error || "Verifica non trovata."}</p>
      <button className="btn btn-outline" style={{ marginTop: "var(--space-4)" }} onClick={() => navigate("/deleghe/stats")}>← Torna alla lista</button>
    </div>
  );

  const isLegittimato = check.verdetto_stato === "LEGITTIMATO";
  const canAvviaFirma = isLegittimato && check.has_documento && !check.firma_status;

  return (
    <DetailPageShell
      backTo="/deleghe/stats"
      backLabel="Lista deleghe"
      title={
        <>
          <span className="material-symbols-outlined" style={{ marginRight: "var(--space-2)", verticalAlign: "middle" }}>verified_user</span>
          Verifica Delega
        </>
      }
      titleId={check.id}
      badges={<VerdettoBadge stato={check.verdetto_stato} />}
      meta={check.completed_at ? <span>Verificata il {fmt(check.completed_at)}</span> : undefined}
    >
      <div style={{ maxWidth: 860, width: "100%", paddingBottom: "var(--space-4)" }}>

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: "var(--space-4)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>error</span>
          {error}
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => setError(null)}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      )}

      {/* Warning: visura analizzata in più parti per dimensione eccessiva */}
      {(check.analysis_warnings ?? []).map((w, i) => (
        <div key={i} className="alert alert-warning" style={{ alignItems: "flex-start", marginBottom: "var(--space-4)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>warning</span>
          <span>{w}</span>
        </div>
      ))}

      {/* Persone — a viewport stretti collassa a 1 colonna (altrimenti le
          colonne <~150px spezzano i valori un carattere per riga). */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "var(--space-4)", marginBottom: "var(--space-5)" }}>
        <div className="card">
          <div style={{ ...sectionLabelStyle, marginBottom: "var(--space-3)" }}>
            Delegante (chi firma per primo)
          </div>
          <InfoRow label="Nome" value={`${check.delegante_nome} ${check.delegante_cognome}`.trim()} />
          <InfoRow label="Email" value={check.delegante_email} />
          <InfoRow label="Telefono" value={check.delegante_telefono} />
          <InfoRow label="Codice Fiscale" value={check.delegante_cf} />
          <InfoRow label="Data di Nascita" value={check.delegante_data_nascita} />
        </div>
        <div className="card">
          <div style={{ ...sectionLabelStyle, marginBottom: "var(--space-3)" }}>
            Delegato (chi firma per secondo)
          </div>
          <InfoRow label="Nome" value={`${check.delegato_nome} ${check.delegato_cognome}`.trim()} />
          <InfoRow label="Email" value={check.delegato_email} />
          <InfoRow label="Telefono" value={check.delegato_telefono} />
          <InfoRow label="Codice Fiscale" value={check.delegato_cf} />
          <InfoRow label="Data di Nascita" value={check.delegato_data_nascita} />
        </div>
      </div>

      {/* Attività + date */}
      <div className="card" style={{ marginBottom: "var(--space-5)" }}>
        <InfoRow label="Attività" value={check.attivita} />
        <InfoRow label="Verificata il" value={fmt(check.completed_at)} />
        {check.firma_avviata_at && <InfoRow label="Firma avviata il" value={fmt(check.firma_avviata_at)} />}
        {check.firma_completata_at && <InfoRow label="Firma completata il" value={fmt(check.firma_completata_at)} />}
      </div>

      {/* Motivazione verdetto */}
      {check.verdetto_reasoning && (
        <div className="card" style={{ marginBottom: "var(--space-5)" }}>
          <div style={{ ...sectionLabelStyle, marginBottom: "var(--space-2)" }}>
            Motivazione AI
          </div>
          <p style={{ margin: 0, fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-700)", lineHeight: 1.6 }}>
            {check.verdetto_reasoning}
          </p>
        </div>
      )}

      {/* PDF */}
      {isLegittimato && (
        <div className="card" style={{ marginBottom: "var(--space-5)" }}>
          <div style={{ ...sectionLabelStyle, marginBottom: "var(--space-3)" }}>
            Documento Procura Speciale
          </div>
          {check.has_documento ? (
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
              <button className="btn btn-outline" onClick={handleDownloadPdf} disabled={pdfLoading}>
                {pdfLoading
                  ? <><span className="spinner" style={{ width: 14, height: 14 }} />Caricamento…</>
                  : <><span className="material-symbols-outlined">download</span>Scarica PDF</>
                }
              </button>
              {check.documento_generato_at && (
                <span style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)" }}>
                  Generato il {fmt(check.documento_generato_at)}
                </span>
              )}
            </div>
          ) : (
            <button className="btn btn-primary" onClick={handleGeneraPdf} disabled={pdfLoading}>
              {pdfLoading
                ? <><span className="spinner" style={{ width: 14, height: 14 }} />Generazione…</>
                : <><span className="material-symbols-outlined">picture_as_pdf</span>Genera Procura Speciale</>
              }
            </button>
          )}
        </div>
      )}

      {/* Firma */}
      {isLegittimato && check.has_documento && (
        <div className="card" style={{ marginBottom: "var(--space-5)" }}>
          <div style={{ ...sectionLabelStyle, marginBottom: "var(--space-4)" }}>
            Processo di Firma Digitale
          </div>
          <FirmaBlock
            firmaStatus={canAvviaFirma ? null : (check.firma_status ?? null)}
            deleganteLabel={`${check.delegante_nome} ${check.delegante_cognome}`.trim()}
            delegatoLabel={`${check.delegato_nome} ${check.delegato_cognome}`.trim()}
            waNumber={check.wa_number}
            firmaCompletataAt={check.firma_completata_at}
            firmaLoading={firmaLoading}
            annullaLoading={annullaLoading}
            firmatoLoading={firmatoLoading}
            onAvvia={handleAvviaFirma}
            onRetry={handleAvviaFirma}
            onAnnulla={handleAnnullaFirma}
            onDownloadFirmato={handleDownloadFirmato}
          />
        </div>
      )}

      {/* Verdetto NON LEGITTIMATO — nota */}
      {!isLegittimato && check.verdetto_stato && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", border: `1px solid var(--indaco-danger)` }}>
          <strong style={{ color: "var(--indaco-danger)" }}>Delega non legittimata</strong>
          <p style={{ fontSize: "var(--font-size-sm)", margin: "var(--space-1) 0 0", color: "var(--indaco-gray-700)" }}>
            L'analisi AI non ha rilevato i poteri di firma necessari per l'attività richiesta. Il processo di firma non può essere avviato.
          </p>
        </div>
      )}
      </div>
    </DetailPageShell>
  );
}
